import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Loader2 } from 'lucide-react';
import TierBadge from './TierBadge';
import TierComparison, { cumulativeFeatures } from './TierComparison';
import { API } from '@/App';
import StripeInAppCheckout from './StripeInAppCheckout';
import { isMobileBuild } from '@/lib/mobileBuild';
import UpgradeOnWebNotice from './UpgradeOnWebNotice';

const TIERS = ['silver', 'gold', 'platinum'];

// The fallback if the API is unreachable. The live copy is edited in Admin → Tier Pricing.
const tierFeatures = {
  silver: { name: 'Silver', modules: 'Modules 1-4', features: ['Access to Modules 1-4', 'Consciousness Companion (journal, worksheets, milestones)', 'Forum categories 1-4', 'Calendar & Events', 'Resources', 'Messages', 'Workshops'] },
  gold: { name: 'Gold', modules: 'Modules 1-8', features: ['Access to Modules 1-8', 'Consciousness Companion (journal, worksheets, milestones)', 'Forum categories 1-7', 'Live Stream Access', 'Calendar & Events', 'Resources', 'Messages', 'Workshops'] },
  platinum: { name: 'Platinum', modules: 'All 13 Modules', features: ['Access to ALL Modules', 'Consciousness Companion (journal, worksheets, milestones)', 'All 10 Forum Categories', 'Live Stream Access', 'Calendar & Events', 'Resources', 'Messages', 'Workshops', 'Platinum+ area'] },
};

const money = (value) => `$${Number(value || 0).toFixed(2)}`;

const TierPaymentModal = ({ isOpen, onClose, onSuccess, currentTier = 'bronze', suggestedTier = null, returnTo = null, returnLabel = 'where you left off' }) => {
  const [selectedTier, setSelectedTier] = useState(suggestedTier || 'silver');
  // Members compare first, then price one tier, then pay.
  const [step, setStep] = useState('compare');
  const [option, setOption] = useState('full');
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState('');
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [maintenance, setMaintenance] = useState(null);
  const [included, setIncluded] = useState(null);
  const [prices, setPrices] = useState(null);
  const navigate = useNavigate();

  const fetchQuote = useCallback(async (tier) => {
    setLoadingQuote(true);
    setQuoteError('');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/membership/quote/${tier}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQuote(res.data);
      setOption(res.data.options.plan ? 'full' : 'full');
    } catch (error) {
      setQuote(null);
      setQuoteError(error.response?.data?.detail || 'We could not price this upgrade.');
    } finally {
      setLoadingQuote(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    fetchQuote(selectedTier);
    axios.get(`${API}/maintenance-fee`)
      .then((r) => setMaintenance(r.data.fee ?? r.data.amount))
      .catch(() => {});
    axios.get(`${API}/membership/tier-features`)
      .then((r) => setIncluded(r.data))
      .catch(() => {});
    axios.get(`${API}/membership/price-table`)
      .then((r) => setPrices(r.data))
      .catch(() => {});
  }, [isOpen, selectedTier, fetchQuote]);

  const finishUpgrade = async (message) => {
    try {
      const token = localStorage.getItem('token');
      const me = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      localStorage.setItem('user', JSON.stringify(me.data));
    } catch (error) {
      console.error('Failed to refresh user after upgrade', error);
    }
    toast.success(message);
    onSuccess?.();
    onClose();
    setTimeout(() => window.location.reload(), 1200);
  };

  if (isMobileBuild()) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-2xl font-bold">Upgrade Your Membership</DialogTitle></DialogHeader>
          <UpgradeOnWebNotice
            title="Upgrade your membership on our website"
            description="Membership upgrades are handled securely on our website. Upgrade there once, then enjoy everything right here in the app."
            ctaLabel="Upgrade on Website"
            path="/dashboard/modules"
            testId="tier-upgrade-web-notice"
          />
        </DialogContent>
      </Dialog>
    );
  }

  const plan = quote?.options?.plan;
  const full = quote?.options?.full;
  const chosenTotal = option === 'plan' ? plan?.first_payment?.billed : full?.total;
  // What the server actually priced, so a discount never leaves two totals on screen.
  const [pricedTotal, setPricedTotal] = useState(null);
  const [discountApplied, setDiscountApplied] = useState(null);

  // Switching tier or payment option re-prices from scratch; a discount from the
  // previous choice must not linger on the summary card.
  useEffect(() => {
    setPricedTotal(null);
    setDiscountApplied(null);
  }, [option, selectedTier]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="tier-payment-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            {showPayment ? 'Complete Your Purchase'
              : step === 'compare' ? 'Compare Your Membership Options' : 'Choose Your Membership'}
          </DialogTitle>
          <DialogDescription>
            {showPayment
              ? 'Enter your card details to finish. Your card is kept on file for your plan and annual maintenance.'
              : step === 'compare'
                ? 'What each tier adds, side by side. Pick one to see your price with any credit applied.'
                : 'Pay in full, or spread it across the coaching program month by month.'}
          </DialogDescription>
        </DialogHeader>

        {showPayment ? null : step === 'compare' ? (
          <>
            {returnTo && (
              <div className="text-left">
                <button
                  type="button"
                  onClick={() => { onClose(); navigate(returnTo); }}
                  className="text-sm text-cyan-700 hover:underline"
                  data-testid="modal-return-link"
                >
                  ← Back to {returnLabel}
                </button>
              </div>
            )}
            <TierComparison
              copy={included}
              prices={prices}
              currentTier={currentTier}
              suggestedTier={suggestedTier}
              loading={!included || !prices}
              onChoose={(chosen) => { setSelectedTier(chosen); setStep('tier'); }}
            />
          </>
        ) : (
          <div className="space-y-4">
            <button
              type="button"
              onClick={() => setStep('compare')}
              className="text-sm text-cyan-700 hover:underline"
              data-testid="back-to-compare-btn"
            >
              ← Compare all tiers
            </button>
            {(() => {
              const tier = selectedTier;
              return (
                <Card className="border-2 border-cyan-500">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TierBadge tier={tier} size="md" />
                      <span>{tierFeatures[tier].name} Membership</span>
                    </CardTitle>
                    <p className="text-sm text-gray-500">{included?.tiers?.[tier]?.modules_label || tierFeatures[tier].modules}</p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {loadingQuote ? (
                      <div className="py-10 text-center text-gray-500">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Working out your price…
                      </div>
                    ) : quoteError ? (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900" data-testid="quote-error">
                        {quoteError}
                      </div>
                    ) : quote && (
                      <>
                        {quote.credit > 0 && (
                          <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm" data-testid="upgrade-credit">
                            <p className="text-green-800 font-semibold">
                              Your credit: {money(quote.credit)}
                            </p>
                            <p className="text-green-700">
                              The base you've already paid on {quote.current_tier}. {money(quote.target_base)} −{' '}
                              {money(quote.credit)} = {money(quote.financed)} left to pay.
                            </p>
                          </div>
                        )}

                        <div className="grid gap-3">
                          <Card
                            onClick={() => setOption('full')}
                            className={`cursor-pointer border-2 transition-colors ${option === 'full' ? 'border-cyan-500 bg-cyan-50' : 'border-gray-200 hover:border-cyan-300'}`}
                            data-testid="option-full"
                          >
                            <CardContent className="p-4">
                              <div className="flex justify-between items-start mb-1">
                                <div>
                                  <h3 className="font-semibold">Pay in full</h3>
                                  <p className="text-xs text-gray-600">No financing fee — the lowest total.</p>
                                </div>
                                {option === 'full' && <Check className="w-5 h-5 text-cyan-600" />}
                              </div>
                              <p className="text-3xl font-bold text-cyan-600">{money(full?.total)}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {money(quote.financed)} + {quote.processing_pct}% processing
                              </p>
                            </CardContent>
                          </Card>

                          {plan ? (
                            <Card
                              onClick={() => setOption('plan')}
                              className={`cursor-pointer border-2 transition-colors ${option === 'plan' ? 'border-cyan-500 bg-cyan-50' : 'border-gray-200 hover:border-cyan-300'}`}
                              data-testid="option-plan"
                            >
                              <CardContent className="p-4">
                                <div className="flex justify-between items-start mb-1">
                                  <div>
                                    <h3 className="font-semibold">{plan.count} monthly payments</h3>
                                    <p className="text-xs text-gray-600">
                                      First payment today, then the same day each month.
                                    </p>
                                  </div>
                                  {option === 'plan' && <Check className="w-5 h-5 text-cyan-600" />}
                                </div>
                                <p className="text-3xl font-bold text-cyan-600">
                                  {money(plan.first_payment.billed)}
                                  <span className="text-sm font-normal text-gray-500"> / month</span>
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {plan.count} payments · {money(plan.plan_total)} total · includes the{' '}
                                  {quote.convenience_pct}% financing fee and {quote.processing_pct}% processing
                                </p>
                              </CardContent>
                            </Card>
                          ) : (
                            <div className="text-xs text-gray-500 border border-gray-200 rounded-lg p-3" data-testid="no-plan-note">
                              You've already made all {quote.target_plan_count} plan payments toward your
                              coaching program, so this upgrade is pay-in-full only.
                            </div>
                          )}
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900 space-y-1">
                          <p>
                            Your maintenance anniversary never changes — upgrading doesn't reset it.
                          </p>
                          <p className="text-xs text-blue-800">
                            After 12 months as a paid member, maintenance is{' '}
                            {money(((maintenance ?? 11100) / 100) * (1 + (quote.processing_pct || 3) / 100))} a
                            year.
                          </p>
                        </div>
                      </>
                    )}

                    <div className="border-t pt-4">
                      <h4 className="font-semibold mb-2">What's included</h4>
                      <ul className="space-y-1.5">
                        {(included?.tiers ? cumulativeFeatures(included.tiers, tier) : tierFeatures[tier].features).map((feature) => (
                          <li key={feature} className="flex items-start gap-2 text-sm">
                            <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Button
                      onClick={() => setShowPayment(true)}
                      disabled={!quote || loadingQuote}
                      className="w-full bg-cyan-600 hover:bg-cyan-700 text-white text-lg py-6"
                      data-testid="continue-to-payment-btn"
                    >
                      Continue to Payment
                    </Button>
                  </CardContent>
                </Card>
              );
            })()}
          </div>
        )}

        {showPayment && (
          <div className="space-y-4">
            <Card className="bg-gradient-to-r from-cyan-50 to-blue-50">
              <CardContent className="p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold">Selected</span>
                  <div className="flex items-center gap-2">
                    <TierBadge tier={selectedTier} size="sm" />
                    <span>{option === 'plan' ? `${plan?.count} monthly payments` : 'Pay in full'}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center text-2xl font-bold text-cyan-600">
                  <span>{option === 'plan' ? 'Due today' : 'Total'}</span>
                  <span data-testid="checkout-total">
                    {money(pricedTotal ?? chosenTotal)}
                  </span>
                </div>
                {discountApplied && (
                  <p
                    className="text-sm text-right mt-1 font-medium text-emerald-700"
                    data-testid="checkout-summary-discount"
                  >
                    {discountApplied.label} — {money(discountApplied.amount_off)} off
                    <span className="ml-2 text-gray-500 line-through font-normal">
                      {money(chosenTotal)}
                    </span>
                  </p>
                )}
                {option === 'plan' && (
                  <p className="text-sm text-gray-600 text-right mt-1">
                    then {money(plan?.payments?.[1]?.billed || plan?.first_payment?.billed)} a month ·{' '}
                    {money(plan?.plan_total)} in total
                  </p>
                )}
              </CardContent>
            </Card>

            <StripeInAppCheckout
              itemType="tier_upgrade"
              tier={selectedTier}
              paymentOption={option}
              isAddon={false}
              onSuccess={() => finishUpgrade('Membership updated!')}
              onFreeUpgrade={() => finishUpgrade('Your credit covered this upgrade!')}
              onError={(msg) => toast.error(msg)}
              onPriced={({ amountCharged, discount }) => {
                setPricedTotal(amountCharged);
                setDiscountApplied(discount);
              }}
            />

            <Button
              onClick={() => { setShowPayment(false); setPricedTotal(null); setDiscountApplied(null); }}
              variant="outline"
              className="w-full"
            >
              Back
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TierPaymentModal;
